import { type BottomTabBarProps } from '@react-navigation/bottom-tabs'
import { clsx } from 'clsx'
import { Image } from 'expo-image'
import { map } from 'lodash'
import plur from 'plur'
import { useTransition } from 'react'
import { loadQuery, useRelayEnvironment } from 'react-relay'

import logo from '@/assets/images/logo.png'
import { HStack, PlatformPressable, RedBadge, Text, View } from '@/components'
import {
  COLOR_WHITE,
  HEADER_HEIGHT,
  RelayQueryMap,
  relayQueryNodes,
  saveRelayPreloadedQuery,
  type RelayQueryName
} from '@/lib'

const routeToQueryMap: Record<string, RelayQueryName> = {
  account: 'AccountScreenQuery',
  likes: 'LikesScreenQuery',
  messages: 'MessagesScreenQuery',
  profile: 'OwnProfileScreenQuery',
  search: 'SearchScreenQuery'
}

export const TabBar = ({ descriptors, navigation, state }: BottomTabBarProps) => {
  const environment = useRelayEnvironment()
  const [, startTransition] = useTransition()
  const { tabBarPosition } = descriptors[state.routes[state.index].key].options

  const isSidebar = tabBarPosition === 'left'

  return (
    <View
      accessibilityLabel='Main navigation'
      accessibilityRole='tablist'
      className={clsx(
        'flex p-1',
        isSidebar
          ? 'py-safe-or-1 w-56 flex-col gap-2 border-r-[0.5px] border-r-brand-purple-500/40'
          : 'pb-safe-or-0 flex flex-row overflow-hidden rounded-t-2xl bg-brand-black-500'
      )}
    >
      {isSidebar ? (
        <HStack
          style={{
            height: HEADER_HEIGHT
          }}
        >
          <Image
            alt='Dateability - Making Love Accessible'
            className='h-full w-full'
            contentFit='contain'
            contentPosition='center'
            source={logo}
          />
        </HStack>
      ) : null}
      {map(state.routes, (route, idx) => {
        const { options } = descriptors[route.key]
        const focused = state.index === idx
        const labelText =
          options.tabBarLabel !== undefined
            ? (options.tabBarLabel as string)
            : options.title !== undefined
              ? options.title
              : route.name
        const badgeCount = options.tabBarBadge ? Number(options.tabBarBadge) : 0

        return (
          <PlatformPressable
            accessibilityLabel={
              options.tabBarAccessibilityLabel ??
              `${labelText} tab${options.tabBarBadge ? `. ${badgeCount} new ${plur('item', badgeCount)}` : ''}`
            }
            accessibilityRole='tab'
            accessibilityState={{ selected: focused }}
            className={clsx(
              'items-center',
              isSidebar ? 'w-full flex-row gap-3 rounded-lg p-2' : 'flex-1 gap-0.5 rounded-lg p-1',
              focused && 'bg-brand-orange-500'
            )}
            key={route.key}
            testID={`TabBar.${route.name}Tab`}
            onLongPress={() =>
              navigation.emit({
                type: 'tabLongPress',
                target: route.key
              })
            }
            onPress={() => {
              const event = navigation.emit({
                type: 'tabPress',
                target: route.key,
                canPreventDefault: true
              })

              if (!focused && !event.defaultPrevented) {
                const queryName = routeToQueryMap[route.name]
                const preloadedQuery = loadQuery<RelayQueryMap[typeof queryName]>(
                  environment,
                  relayQueryNodes[queryName],
                  {}
                )

                saveRelayPreloadedQuery(queryName, {}, preloadedQuery)

                startTransition(() => navigation.navigate(route.name, route.params))
              }
            }}
            role='tab'
          >
            {options?.tabBarIcon?.({ color: COLOR_WHITE, focused, size: 20 })}
            <Text
              className={clsx(
                'select-none text-white',
                isSidebar ? 'flex-1 font-poppinsSemiBold text-lg' : 'font-poppinsMedium text-xs'
              )}
              numberOfLines={1}
            >
              {labelText}
            </Text>
            {badgeCount ? (
              <RedBadge
                className={clsx(!isSidebar && 'absolute right-1/4 top-0 h-4 min-w-4', focused && 'bg-transparent')}
                count={badgeCount}
                textClassName={!isSidebar ? 'text-2xs' : ''}
              />
            ) : null}
          </PlatformPressable>
        )
      })}
    </View>
  )
}
